import React, {useMemo} from 'react';

import copyToClipboard from 'copy-to-clipboard';
import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {DateRange} from 'components/ui-kit/DateRange';
import {DropdownMenu} from 'components/ui-kit/DropdownMenu';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {CopyButtonUniqueId, StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {APPEAL_STATUS_COLORS} from 'core/constants/statuses';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {profile$} from 'models/auth';
import {
    complaintsState$,
    exportRequested,
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    pageSizeChanged,
    sortingsChanged,
} from 'models/complaints';
import {colors} from 'theme';
import {
    AppealRegistryItem,
    AppealExportFormat,
    AppealStatus,
    ComplaintsAccessors,
    ComplaintsFormattedRow,
    GridFieldConfig,
    UserPermission,
    UserRoles,
} from 'types';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {hasAccess, hasAccessByRoles} from 'utils/roles';

import {CodeNotFoundBadge, MarkingCode, MarkingCodeText, SlaBadge, Toolbar} from './styled';

const statusOptions = (t: TFunction) =>
    Object.values(AppealStatus).map((status) => ({
        id: status,
        value: status,
        label: t(`pages.complaints.statuses.${status}`),
    }));

const getGridColumns = (
    t: TFunction,
    themeOptions: Array<{label: string; value: string}>,
    showAssignee: boolean,
    showStore: boolean,
): GridFieldConfig[] => [
    {
        id: ComplaintsAccessors.id,
        header: t('pages.complaints.fields.publicNo'),
        width: 130,
    },
    {
        id: ComplaintsAccessors.theme,
        header: t('pages.complaints.fields.theme'),
        filter: <Select isClearable options={themeOptions} />,
        isSortable: true,
        width: 180,
    },
    {
        id: ComplaintsAccessors.createdAt,
        header: t('pages.complaints.fields.date'),
        filter: <DateRange />,
        width: 165,
    },
    ...(showAssignee
        ? [
              {
                  id: ComplaintsAccessors.assigneeName,
                  header: t('pages.complaints.fields.assignee'),
                  width: 190,
              },
          ]
        : []),
    ...(showStore
        ? [
              {
                  id: ComplaintsAccessors.store,
                  header: t('pages.complaints.fields.store'),
                  filter: <Input placeholder={t('pages.complaints.filters.store')} />,
                  isSortable: true,
                  width: 190,
              },
          ]
        : []),
    // ...(canFilterEmployees
    //     ? [
    //           {
    //               id: ComplaintsAccessors.assigneeName,
    //               header: t('pages.complaints.fields.assignee'),
    //               filter: <Select isClearable options={employeeOptions} />,
    //               width: 190,
    //           },
    //       ]
    //     : []),
    // ...(canFilterBranches
    //     ? [
    //           {
    //               id: ComplaintsAccessors.branchName,
    //               header: t('pages.complaints.fields.branch'),
    //               filter: <Select isClearable options={branchOptions} />,
    //               width: 180,
    //           },
    //       ]
    //     : []),
    {
        id: ComplaintsAccessors.markingCode,
        header: t('pages.complaints.fields.markingCode'),
        // filter: <Input placeholder={t('pages.complaints.filters.markingCode')} />,
        width: 140,
    },
    {
        id: ComplaintsAccessors.codeNotFound,
        header: t('pages.complaints.fields.codeNotFound'),
        // filter: (
        //     <Select
        //         isClearable
        //         options={[
        //             {label: t('pages.complaints.filters.yes'), value: true},
        //             {label: t('pages.complaints.filters.no'), value: false},
        //         ]}
        //     />
        // ),
        width: 150,
    },
    {
        id: ComplaintsAccessors.slaIndicator,
        header: t('pages.complaints.fields.sla'),
        // filter: (
        //     <Select
        //         isClearable
        //         options={[
        //             {label: t('pages.complaints.filters.overdue'), value: true},
        //             {label: t('pages.complaints.filters.notOverdue'), value: false},
        //         ]}
        //     />
        // ),
        width: 115,
    },
    {
        id: ComplaintsAccessors.status,
        header: t('pages.complaints.fields.status'),
        filter: <CheckboxArray name="complaint-status" options={() => statusOptions(t)} />,
        width: 150,
    },
];

const copyMarkingCode = (markingCode: string, t: TFunction) => {
    copyToClipboard(String(markingCode ?? ''));
    toast.success(t('pages.complaints.markingCodeCopied'));
};

const formatEmployeeName = (employee: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
}) =>
    [employee.lastName, employee.firstName, employee.middleName].filter(Boolean).join(' ') ||
    employee.email ||
    '—';

const convertData = (
    data: AppealRegistryItem[] | undefined,
    themeNames: Map<string, string>,
    t: TFunction,
) =>
    (data ?? []).map<ComplaintsFormattedRow>((item) => ({
        [ComplaintsAccessors.id]: (
            <Link to={generatePath(ROUTE.COMPLAINTS_DETAIL, {id: item.id})}>{item.id}</Link>
        ),
        [ComplaintsAccessors.theme]: handleNil(themeNames.get(item.theme) ?? item.theme),
        [ComplaintsAccessors.status]: (
            <StatusWrapper color={APPEAL_STATUS_COLORS[item.status]}>
                {t(`pages.complaints.statuses.${item.status}`)}
            </StatusWrapper>
        ),
        [ComplaintsAccessors.createdAt]: formatDate(item.createdAt, 'dd.MM.yyyy HH:mm'),
        [ComplaintsAccessors.assigneeName]: handleNil(item.assigneeName),
        [ComplaintsAccessors.branchName]: handleNil(item.branchName),
        [ComplaintsAccessors.store]: handleNil(item.storeName ?? item.storeAddress),
        [ComplaintsAccessors.markingCode]: (
            <MarkingCode>
                <MarkingCodeText>{handleNil(item.markingCode)}</MarkingCodeText>
                <CopyButtonUniqueId
                    className="copy-button"
                    tooltipId={`marking-code-${item.id}`}
                    icon={
                        <Icon
                            source={CopyIcon}
                            width={15}
                            height={15}
                            fillColor={colors.brandPrimary}
                        />
                    }
                    onClick={() => copyMarkingCode(item.markingCode ?? '', t)}
                    hoverIndication={true}
                    label={t('pages.orders.detail.common.copyId')}
                    labelPlace={'bottom'}
                />
            </MarkingCode>
        ),
        [ComplaintsAccessors.codeNotFound]: item.codeNotFound ? (
            <CodeNotFoundBadge>{t('pages.complaints.codeNotFound')}</CodeNotFoundBadge>
        ) : (
            '—'
        ),
        [ComplaintsAccessors.slaIndicator]: item.slaIndicator ? (
            <SlaBadge indicator={item.slaIndicator} title={item.slaDueAt ?? undefined}>
                {t(`pages.complaints.sla.${item.slaIndicator}`)}
            </SlaBadge>
        ) : (
            '—'
        ),
        raw: item,
    }));

export const ComplaintsPage = () => {
    const {t} = useTranslation();
    const profile = useStore(profile$);
    const state = useStore(complaintsState$);
    const permissions = profile?.permissions ?? [];
    const canViewAll = hasAccess(permissions, [UserPermission.APPEAL_ALL_LIST]);
    // const canCreate = hasAccess(permissions, [UserPermission.APPEAL_CREATE]);
    const canViewPersonnel = permissions.includes(UserPermission.PERSONNEL_NK);
    const canManageBranches = permissions.includes(UserPermission.NK_BRANCH_MANAGE);
    const canFilterEmployees = canViewAll && canViewPersonnel;
    const canFilterBranches = canViewAll && canManageBranches;
    const showAssignee = hasAccessByRoles([UserRoles.ROLE_MPL_NK_DIRECTOR]);
    const showStore = hasAccessByRoles([UserRoles.ROLE_MPL_NK_EMPLOYEE]);

    useGate(Gate, {canViewPersonnel, canManageBranches});

    const employeeOptions = useMemo(
        () =>
            state.employees.map((employee) => ({
                label: formatEmployeeName(employee),
                value: employee.userId,
            })),
        [state.employees],
    );
    const branchOptions = useMemo(
        () => state.branches.map((branch) => ({label: branch.name, value: branch.id})),
        [state.branches],
    );
    const themeOptions = useMemo(
        () => state.themes.map((theme) => ({label: theme.name, value: theme.code})),
        [state.themes],
    );
    const themeNames = useMemo(
        () => new Map(state.themes.map((theme) => [theme.code, theme.name])),
        [state.themes],
    );
    const columns = useMemo(
        () =>
            mapTableColumns(
                getGridColumns(t, themeOptions, showAssignee, showStore),
                state.filters,
                filtersChanged,
                filtersReset,
                state.sortings,
                sortingsChanged,
            ),
        [
            t,
            canFilterEmployees,
            canFilterBranches,
            themeOptions,
            employeeOptions,
            branchOptions,
            state.filters,
            state.sortings,
        ],
    );
    const data = useMemo(() => convertData(state.data?.data, themeNames, t), [
        state.data?.data,
        themeNames,
        t,
    ]);
    return (
        <Page title={t('menu.Complaints')}>
            <Table
                columns={columns}
                data={data}
                totalRowsCount={state.data?.totalElements}
                pageCount={state.data?.totalPages}
                pageIndex={state.data?.page}
                onPageChange={pageNumberChanged}
                onPageSizeChange={pageSizeChanged}
                isLoading={state.isLoading}
                hideSetting
                showExpandableColumn={false}
                headerControls={
                    <Toolbar>
                        <DropdownMenu
                            label={t('components.endlessList.export')}
                            buttonSize="sm"
                            buttonVariant={ButtonVariant.Secondary}
                        >
                            <DropdownMenu.List>
                                {Object.values(AppealExportFormat).map((format) => (
                                    <DropdownMenu.Item
                                        key={format}
                                        disabled={state.isExporting}
                                        onSelect={() => exportRequested(format)}
                                    >
                                        {format === AppealExportFormat.EXCEL ? 'XLSX' : format}
                                    </DropdownMenu.Item>
                                ))}
                            </DropdownMenu.List>
                        </DropdownMenu>
                        {/* {canCreate && (
                            <Button size="sm" onClick={() => history.push(ROUTE.COMPLAINTS_CREATE)}>
                                {t('pages.complaints.create.button')}
                            </Button>
                        )} */}
                    </Toolbar>
                }
            />
        </Page>
    );
};
